/* ================================================================
   Änderungen zwischen zwei Fassungen eines Katalogobjekts — für die
   Anzeige beim Admin (Vorschläge prüfen), beim Nutzer (Meine
   Vorschläge) und in der Änderungshistorie.

   Vorher zeigte die Liste nur die einfachen Felder und schrieb bei
   Weinen „Nur Jahrgänge oder Rebsorten betroffen“ — gerade dort
   stecken aber Trinkfenster, Analysewerte und Etiketten. Und ein
   geänderter Winzer erschien als zwei UUIDs.

   Hier wird nur ermittelt, WAS sich unterscheidet, mit der Art des
   Werts. Wie ein Wert aussieht (Ländername, Typbezeichnung, Name zu
   einer Kennung), entscheidet die Oberfläche — dort sitzt die
   Übersetzung.
   ================================================================ */

import { gleich } from "./vorschlaege.js";

/* Feld → [Beschriftung (t()-Schlüssel), Art des Werts]. Die
   Reihenfolge ist die der Anzeige. */
const FELDER = [
  ["name", "Name", "text"],
  ["typ", "Typ", "typ"],
  ["winzerId", "Winzer", "bezug"],
  ["regionId", "Region", "bezug"],
  ["land", "Land", "land"],
  ["ort", "Ort", "text"],
  ["appellation", "Appellation", "text"],
  ["lat", "Breitengrad", "zahl"],
  ["lon", "Längengrad", "zahl"],
  ["rebsortenListe", "Rebsorten", "rebsorten"],
  ["beschreibung", "Beschreibung", "text"],
];

const JAHRGANG_FELDER = [
  ["trinkfenster", "Trinkfenster", "fenster"],
  ["alkohol", "Alkohol", "zahl"],
  ["saeure", "Säure", "zahl"],
  ["restzucker", "Restzucker", "zahl"],
  ["ph", "pH", "zahl"],
  ["schwefel", "Schwefel", "text"],
  ["foto", "Etikett", "foto"],
];

const leer = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);
const gleichWert = (a, b) => (leer(a) && leer(b)) || gleich(a, b);

function jahrgangWert(j, feld) {
  if (!j) return null;
  if (feld === "trinkfenster") {
    return (j.trinkVon == null && j.trinkBis == null) ? null : { von: j.trinkVon ?? null, bis: j.trinkBis ?? null };
  }
  if (feld === "foto") {
    const f = j.foto || j.katalogFoto || null;
    return f ? (f.pfad || f.thumb || null) : null;
  }
  return j[feld] ?? null;
}


/**
 * Unterschiede zweier Fassungen.
 *
 * Liefert { felder: [{ feld, label, art, alt, neu }],
 *           jahrgaenge: [{ id, jahr, aenderung: "neu"|"entfernt"|"geaendert",
 *                          felder: [...] }] }
 *
 * `vorher` darf fehlen (Aufnahme) — dann wird nur gezeigt, was
 * gesetzt ist.
 */
function unterschiede(vorher, nachher) {
  const a = vorher || {};
  const b = nachher || {};
  const felder = [];
  for (const [feld, label, art] of FELDER) {
    if (!(feld in a) && !(feld in b)) continue;
    if (gleichWert(a[feld], b[feld])) continue;
    felder.push({ feld, label, art, alt: leer(a[feld]) ? null : a[feld], neu: leer(b[feld]) ? null : b[feld] });
  }

  const jahrgaenge = [];
  if (Array.isArray(a.jahrgaenge) || Array.isArray(b.jahrgaenge)) {
    const alt = new Map((a.jahrgaenge || []).map((j) => [j.id, j]));
    const neu = new Map((b.jahrgaenge || []).map((j) => [j.id, j]));
    const ids = [...new Set([...alt.keys(), ...neu.keys()])];
    for (const id of ids) {
      const ja = alt.get(id) || null;
      const jn = neu.get(id) || null;
      const jfelder = [];
      for (const [feld, label, art] of JAHRGANG_FELDER) {
        const x = jahrgangWert(ja, feld);
        const y = jahrgangWert(jn, feld);
        if (gleichWert(x, y)) continue;
        jfelder.push({ feld, label, art, alt: x, neu: y });
      }
      const jahr = (jn || ja).jahr;
      const jahrGeaendert = ja && jn && ja.jahr !== jn.jahr;
      if (jahrGeaendert) jfelder.unshift({ feld: "jahr", label: "Jahr", art: "jahr", alt: ja.jahr, neu: jn.jahr });
      if (!ja) jahrgaenge.push({ id, jahr, aenderung: "neu", felder: jfelder.filter((f) => f.neu != null) });
      else if (!jn) jahrgaenge.push({ id, jahr, aenderung: "entfernt", felder: [] });
      else if (jfelder.length > 0) jahrgaenge.push({ id, jahr, aenderung: "geaendert", felder: jfelder });
    }
    /* Jüngste zuerst, N.V. am Ende — wie überall in der Anwendung. */
    const rang = (j) => (typeof j.jahr === "number" ? j.jahr : -Infinity);
    jahrgaenge.sort((x, y) => rang(y) - rang(x));
  }
  return { felder, jahrgaenge };
}

const istLeer = (u) => u.felder.length === 0 && u.jahrgaenge.length === 0;

export { FELDER, JAHRGANG_FELDER, istLeer, unterschiede };
